/**
 * Une case où l'on tape un montant.
 *
 * ## Le défaut que ce composant corrige
 *
 * Signalé le 19 septembre 2026 sur les cases « À affecter » :
 *
 *   « Quand je saisis 30 il saisit 3,01 ; je dois manuellement retirer le 0,1
 *     et remettre un 0 après le trois. »
 *
 * La case était un `<input type="number">` contrôlé, dont la valeur était
 * **reformatée à chaque frappe** : taper `3` produisait l'état `300`, aussitôt
 * réécrit `3.00` dans la case. Le curseur repart alors à la fin — un
 * `input[type=number]` n'expose pas sa sélection, donc rien ne peut la
 * restaurer — et le chiffre suivant tombe derrière les décimales.
 *
 * Et `type="number"` laisse la **molette** et les **flèches** modifier la
 * valeur d'un `step`. Sur une case de montant, dans une page qui défile, un
 * centime peut changer sans qu'on ait rien tapé.
 *
 * ## Les trois règles de ce composant
 *
 * 1. **Ce qui est tapé reste tel quel tant qu'on tape.** Le texte de
 *    l'utilisateur est l'état ; les centimes sont calculés à côté. Une case
 *    qui réécrit ce qu'on vient d'y taper ne peut pas être remplie.
 * 2. **La remise en forme attend la sortie du champ.** `30` devient `30,00`
 *    quand on en part, jamais pendant.
 * 3. **`type="text"` avec `inputMode="decimal"`.** Le clavier numérique
 *    apparaît sur mobile, et ni la molette ni les flèches ne peuvent plus
 *    changer un montant.
 *
 * La lecture, elle, vit dans `montant.ts` : sans réseau, sans navigateur, et
 * éprouvée.
 */

import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { centimesSaisis, saisieDepuisCentimes } from '../../../lib/payments/montant';

export default function SaisieMontant({
  valeurCents, onChange, ariaLabel, className, disabled,
}: {
  /** Le montant actuel, en centimes. `0` affiche une case vide. */
  valeurCents: number;
  /**
   * Appelé à chaque frappe lisible. Une saisie illisible — `abc`, une case
   * vidée — rend `0` : la répartition ne doit pas garder un montant que la
   * case ne montre plus.
   */
  onChange: (centimes: number) => void;
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
}) {
  const [texte, setTexte] = useState(() => (valeurCents ? saisieDepuisCentimes(valeurCents) : ''));
  const [enSaisie, setEnSaisie] = useState(false);

  /*
    La resynchronisation depuis l'extérieur — une répartition remplacée par
    l'écran, une ligne rechargée — n'a lieu QUE hors saisie. Pendant la frappe,
    le texte de l'utilisateur fait foi : c'est toute la correction.
  */
  useEffect(() => {
    if (enSaisie) return;
    setTexte(valeurCents ? saisieDepuisCentimes(valeurCents) : '');
  }, [valeurCents, enSaisie]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={texte}
      disabled={disabled}
      aria-label={ariaLabel}
      placeholder="0,00"
      onFocus={() => setEnSaisie(true)}
      onChange={(e) => {
        setTexte(e.target.value);
        onChange(centimesSaisis(e.target.value) ?? 0);
      }}
      onBlur={() => setEnSaisie(false)}
      className={clsx(
        'rounded border border-gray-300 px-2 py-1 text-right text-sm tabular-nums',
        className,
      )}
    />
  );
}
